import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { User } from "../model/User";
import { useHandelUser } from "../hooks/user/useHandelUser";
import Loader from "../components/shared/Loader";
import { SocketState } from "./socket";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  FacebookAuthProvider,
  Auth,
} from "firebase/auth";
import { ROUTES } from "../constants/routes";

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

interface AuthContextType {
  user: User | null;
  setUser: (data: User) => void;
  token: any;
  login: (data: any, redirect?: boolean) => any;
  logout: () => any;
  firebaseAuth: Auth;
  googleProvider: GoogleAuthProvider;
  facebookProvider: FacebookAuthProvider;
}

export const AuthContext = createContext<AuthContextType>(null!);

export const AuthProvider = ({ children }: any) => {
  const { ToGetMe, ToGetUser } = useHandelUser();
  const [token, setToken] = useLocalStorage("token", null);
  const { socket, setSocket } = SocketState();
  const meResponse = ToGetMe();
  const userResponse = ToGetUser(meResponse?.data?.id);

  const [user, setUser] = useState<User | null>(null);

  const navigate = useNavigate();

  // Event listeners for WebSocket events
  const createSocketConnection = (token: string) => {
    if (token) {
      const socket = new WebSocket(
        `${process.env.REACT_APP_SOCKET_URL}?authToken=${token}`
      );
      setSocket(socket);
    }
  };
  const app = initializeApp(firebaseConfig);
  // const analytics = getAnalytics(app);
  const firebaseAuth = getAuth(app);
  const googleProvider = new GoogleAuthProvider();
  const facebookProvider = new FacebookAuthProvider();

  // call this function when you want to authenticate the user
  const login = async (data: any, redirect?: boolean) => {
    setToken(data?.jwt);
    setUser(data.user);
    createSocketConnection(data.jwt);

    if (redirect !== false) {
      const redirectPath = localStorage.getItem("redirect-path");
      if (redirectPath) {
        navigate(redirectPath);
      } else {
        navigate(ROUTES.DASHBOARD_ROUTE);
      }
    }
  };

  // call this function to sign out logged in user
  const logout = () => {
    setToken(null);
    setUser(null);

    if (socket) {
      socket.close();
    }

    navigate(ROUTES.HOME_ROUTE, { replace: true });
  };

  const value = useMemo(
    () => ({
      token,
      login,
      logout,
      user: user,
      setUser,
      firebaseAuth,
      googleProvider,
      facebookProvider,
    }),
    [token, user]
  );

  useEffect(() => {
    if (userResponse?.data) {
      setUser(userResponse.data);
      if (!socket) {
        createSocketConnection(token);
      }
    }
  }, [userResponse?.data]);

  if (userResponse?.isLoading || meResponse?.isLoading) {
    return <Loader className="h-screen" showText={true} />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
